import {
    type ComponentType,
    type Dispatch,
    type FC,
    type ReactElement,
    type ReactNode,
    type RefObject,
    type SetStateAction,
    Suspense,
    type UIEvent,
    useCallback,
    useRef,
    useState
} from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import capitalize from "lodash-es/capitalize"
import { Link } from "react-router-dom"
import { useClickAway, useUpdateEffect } from "react-use"

import { getCurrentAccessToken, getCurrentRefreshToken } from "$/utils/tokens"

import { EPath } from "@/3514/constants"
import { type TLogoutFunction, type TUserState, useLogout, useUser } from "@/3514/hooks"
import { type IUserCoachModel } from "@/3514/types"
import { buildCssClassName, isUserCoach } from "@/3514/utils"
import "@/assets/coach.scss"
import { CARE_TEAM_EMAIL, ServerStateKeys as EQueryKey } from "@/constants"
import { type IUseTranslation, type TUseLogoNavigation, useLogoNavigation, useTranslation } from "@/hooks"
import { CoachCalendarStepUrls } from "@/pages/coach-settings/constants"
import {
    type ISubUserModel as IModel,
    type ISubUserModel,
    type TSubUserListQuery,
    useSubUserListQuery
} from "@/pages/coach-welcome/api"
import { AVATAR_FALLBACK_COLOR } from "@/pages/coach-welcome/config"
import { LocalStorageServiceKeys, StorageService, type TStoredSubUser } from "@/services"
import ProductTypeLogo from "@/shared/ProductTypeLogo"
import Avatar from "@/shared/avatar/Avatar"
import { HeaderDropdownProfileList } from "@/shared/header-dropdown-profile-list"
import FullSpinner from "@/shared/spinner/FullSpinner"
import type { TEmptyCallback } from "@/shared/types/functions"

interface IProps {
    leftSidebar?: ComponentType | ReactElement | null
    headerTitle?: ReactNode
    nav?: ComponentType | ReactElement | null
    wrapperExtraClass?: string
    showLogo?: boolean
    navItem?: ComponentType | ReactElement | null
    align?: "center" | "left"
    size?: "default" | "max-md"
    pageTitle?: ReactNode
    fullWidth?: boolean
    withContainer?: boolean
    children: ReactNode | ReactNode[]
    withoutFooter?: boolean
    withAccessibleDropdown?: boolean
    mainClassName?: string
}

const storageService: StorageService = new StorageService()

/**
 * @deprecated Legacy stuff
 */
const CoachLayout: FC<IProps> = ({
    children,
    leftSidebar,
    pageTitle = null,
    wrapperExtraClass = "",
    showLogo = true,
    align = "left",
    withContainer = true,
    headerTitle = null,
    nav = null,
    navItem = null,
    size = "default",
    fullWidth = false,
    withoutFooter = false,
    withAccessibleDropdown = false,
    mainClassName = String("")
}: IProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const { user }: TUserState = useUser()

    const isCoach: boolean = isUserCoach(user)

    const logout: TLogoutFunction = useLogout()

    const [isActiveMenu, setIsActiveMenu]: [boolean, Dispatch<SetStateAction<boolean>>] = useState<boolean>(false)

    const outsideRefClick: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
    const avatarRef: RefObject<HTMLImageElement | SVGSVGElement> = useRef<HTMLImageElement | SVGSVGElement>(null)

    const toggleActiveMenu: TEmptyCallback = useCallback(
        (): void => setIsActiveMenu((prev: boolean): boolean => !prev),
        []
    )

    const handleLogout: (e: UIEvent) => void = useCallback(
        (e: UIEvent): void => {
            e.preventDefault()
            logout({
                onComplete: async (): Promise<void> =>
                    (user as IUserCoachModel).areSubUsersPresent
                        ? storageService.setItem<boolean>(LocalStorageServiceKeys.MainUserLoggedOut(), true)
                        : void 0
            })
        },
        [user, logout]
    )

    const handleClickOutside: (e: MouseEvent) => void = useCallback(
        (e: MouseEvent): void => e.target !== avatarRef.current && setIsActiveMenu(false),
        []
    )

    const queryClient: QueryClient = useQueryClient()

    const handleSubUserListQuerySuccess: (list: ISubUserModel[]) => void = useCallback(
        (subUserList: ISubUserModel[]): void => {
            queryClient.setQueryData([EQueryKey.SubUsers], (): IModel[] => subUserList)

            storageService.removeItem(LocalStorageServiceKeys.SubUserList())

            const list: TStoredSubUser[] = []

            list.push({
                id: user?.userId ?? user?.id,
                jwtPair: {
                    access: getCurrentAccessToken(),
                    refresh: getCurrentRefreshToken()
                },
                role: capitalize(user.role),
                photo: user.photo,
                firstName: user?.firstName,
                lastName: user?.lastName,
                avatarColor: undefined,
                productType: undefined
            })

            subUserList?.forEach(
                ({ id, jwtPair, color, productType, photo, firstName, lastName }: ISubUserModel): void => (
                    list.push({
                        id,
                        jwtPair,
                        productType,
                        firstName,
                        lastName,
                        role: `${productType} User`,
                        avatarColor: color ?? AVATAR_FALLBACK_COLOR,
                        photo
                    }),
                    void 0
                )
            )

            storageService.setItem<TStoredSubUser[]>(LocalStorageServiceKeys.SubUserList(), list)
        },
        [user, queryClient]
    )

    const { isSuccess: isSubUserListSucceed, data: subUserListData }: TSubUserListQuery = useSubUserListQuery({
        enabled: isUserCoach(user) && user.areSubUsersPresent
    })

    useUpdateEffect(
        (): void => (isSubUserListSucceed && handleSubUserListQuerySuccess(subUserListData), void 0),
        [isSubUserListSucceed, subUserListData]
    )

    useClickAway(outsideRefClick, handleClickOutside)

    const { logoNavigationUrl }: TUseLogoNavigation = useLogoNavigation()

    const avatarDimensions: number = user?.photo ? 30 : 26

    return (
        <div className={buildCssClassName("coach-wrapper", { [wrapperExtraClass]: wrapperExtraClass })}>
            {nav ? (
                <nav className="coach-navbar navbar navbar-expand-lg justify-content-between">
                    <div className="d-flex align-items-center">
                        {showLogo && (
                            <Link to={logoNavigationUrl} className="d-block">
                                <ProductTypeLogo />
                            </Link>
                        )}
                        {navItem as ReactNode}
                    </div>
                    <div className="navbar-container-screen">
                        <div className="coach-nav-header-title">{headerTitle}</div>
                        <div
                            id="navbar"
                            className={buildCssClassName("navbar-collapse ml-lg-auto", {
                                show: false
                            })}
                        >
                            <div className="header-nav-item header-avatar-item d-none d-lg-block position-relative">
                                <div
                                    className={buildCssClassName("dropdown header-nav-dropdown", {
                                        accessible: withAccessibleDropdown
                                    })}
                                >
                                    <button
                                        className="btn dropdown-toggle d-lg-inline-flex m-0"
                                        type="button"
                                        {...(withAccessibleDropdown && { tabIndex: 0 })}
                                        id="userDropdownButton"
                                        data-toggle="dropdown"
                                        aria-expanded={isActiveMenu}
                                        onClick={toggleActiveMenu}
                                    >
                                        <Avatar
                                            ref={avatarRef}
                                            width={avatarDimensions}
                                            height={avatarDimensions}
                                            url={user?.photo}
                                            alt={`photo ${user?.firstName}`}
                                        />
                                    </button>
                                </div>
                                <div
                                    className={buildCssClassName("dropdown-header-menu secondary", {
                                        show: isActiveMenu,
                                        "with-profile-list": (user as IUserCoachModel).areSubUsersPresent
                                    })}
                                    ref={outsideRefClick}
                                >
                                    <a className="dropdown-header-item secondary" href={CARE_TEAM_EMAIL}>
                                        {t("coachSide.header.dropdown.contactCare")}
                                    </a>
                                    {isCoach && (
                                        <Link
                                            className="dropdown-header-item secondary"
                                            to={
                                                (user as IUserCoachModel).isCalendarSetupConfirmed
                                                    ? EPath.Coach_CalendarAvailability()
                                                    : CoachCalendarStepUrls.LINK_CALENDAR_STEP
                                            }
                                        >
                                            {t("coachSide.header.dropdown.calendarSettings")}
                                        </Link>
                                    )}
                                    <span className="dropdown-header-item secondary" onClick={handleLogout}>
                                        {t("common.signOut")}
                                    </span>
                                    {(user as IUserCoachModel).areSubUsersPresent ? (
                                        <HeaderDropdownProfileList />
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            ) : null}
            {leftSidebar as ReactNode}
            <main
                className={buildCssClassName("coach-main", {
                    "w-100": !leftSidebar,
                    "coach-main-s": !fullWidth && !!leftSidebar,
                    "ml-[260px]": fullWidth && !!leftSidebar, // WISER-5808
                    [mainClassName]: mainClassName
                })}
            >
                <div
                    className={buildCssClassName({
                        "container-max-md": size === "max-md",
                        container: withContainer && align === "left" && (!fullWidth || (leftSidebar && fullWidth)),
                        "container-center": align === "center"
                    })}
                >
                    {pageTitle && (
                        <div className="page-title">
                            <h1>{pageTitle}</h1>
                        </div>
                    )}
                    <Suspense fallback={<FullSpinner />}>{children}</Suspense>
                </div>

                {!withoutFooter && <footer className="footer" />}
            </main>
        </div>
    )
}

export { CoachLayout }
